import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { getItemStatusText, getVoucherStatusText } from '@/entities/manual-voucher-approval'
import { useMe } from '@/shared/api/auth'
import {
  ManualVoucherStatus,
  useApproveManualVoucher,
  useManualVoucher,
  useRejectManualVoucher,
} from '@/shared/api/manual-voucher'
import { hasRole, ROLE_CODE_LIST } from '@/shared/const/role-code-list'
import { CustomButton } from '@/shared/ui/button'
import { CardItem } from '@/shared/ui/card-item'
import { PageWrapper } from '@/shared/ui/page-wrapper'
import { getLocalizedText } from '@/shared/utils/locale'

export const Route = createFileRoute('/_auth/applications/manual-voucher-confirmation/$voucherId')({
  staticData: {
    meta: {
      title: 'Карточка заявки на подтверждение фиксации',
      description: '',
    },
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { voucherId } = Route.useParams()

  const {
    data: voucher,
    isLoading,
    isError,
  } = useManualVoucher({
    id: voucherId,
    enabled: !!voucherId,
  })
  const { data: currentUser } = useMe()
  const isOnayAdmin = hasRole(currentUser?.roles, ROLE_CODE_LIST.ADMIN_ONAY_BILIM)
  const { mutate: approveVoucher, isPending: isApproving } = useApproveManualVoucher()
  const { mutate: rejectVoucher, isPending: isRejecting } = useRejectManualVoucher()

  const canApprove = isOnayAdmin && voucher?.status === ManualVoucherStatus.PENDING_DIRECTOR
  const canReject = isOnayAdmin && voucher?.status === ManualVoucherStatus.PENDING_DIRECTOR

  const handleBack = () => {
    navigate({ to: '/applications/manual-voucher-confirmation/list' } as never)
  }

  const handleApprove = () => {
    if (!voucher) return
    approveVoucher({
      manualVoucherId: voucher.id,
    })
  }

  const handleReject = () => {
    if (!voucher) return
    rejectVoucher({
      manualVoucherId: voucher.id,
      reason: t('Отклонено администратором'),
    })
  }

  return (
    <PageWrapper title={t('Карточка заявки на подтверждение фиксации')}>
      <Box display="flex" gap={2} alignItems="center" mb={3}>
        <CustomButton
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIos />}
          onClick={handleBack}
        >
          {t('Назад')}
        </CustomButton>
        <Breadcrumbs separator={<ArrowForwardIos sx={{ fontSize: 16 }} />}>
          <Typography color="text.secondary" key="1">
            {t('Заявки на подтверждение фиксации')}
          </Typography>
          <Typography key="2" color="text.primary">
            {t('Карточка заявки')}
          </Typography>
        </Breadcrumbs>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error">{t('Не удалось загрузить данные заявки')}</Typography>
        </Box>
      )}

      {voucher && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box
            sx={{
              borderRadius: '16px',
              border: '1px solid #E6E6E7',
              backgroundColor: '#F9F9F9',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <CardItem label={t('Статус заявки')} value={getVoucherStatusText(voucher.status, t)} />
            <CardItem label={t('ID заявки')} value={voucher.id} />
            <CardItem
              label={t('Дата создания')}
              value={format(new Date(voucher.createdAt), 'dd.MM.yyyy HH:mm')}
            />
            {voucher.submittedAt && (
              <CardItem
                label={t('Дата отправки')}
                value={format(new Date(voucher.submittedAt), 'dd.MM.yyyy HH:mm')}
              />
            )}
            {voucher.approvedAt && (
              <CardItem
                label={t('Дата утверждения')}
                value={format(new Date(voucher.approvedAt), 'dd.MM.yyyy HH:mm')}
              />
            )}
            {voucher.rejectedAt && (
              <CardItem
                label={t('Дата отклонения')}
                value={format(new Date(voucher.rejectedAt), 'dd.MM.yyyy HH:mm')}
              />
            )}
            <CardItem
              label={t('Дата получения питания')}
              value={format(new Date(voucher.requestedForDate), 'dd.MM.yyyy')}
            />
            <CardItem label={t('Количество записей')} value={voucher.items.length.toString()} />
            {voucher.incidentId && (
              <CardItem label={t('ID инцидента')} value={voucher.incidentId} />
            )}
            {voucher.cancelReason && (
              <CardItem label={t('Причина отмены')} value={voucher.cancelReason} />
            )}
            {voucher.directorComment && (
              <CardItem label={t('Комментарий директора')} value={voucher.directorComment} />
            )}
          </Box>

          {(canApprove || canReject) && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {canApprove && (
                <CustomButton
                  variant="contained"
                  color="primary"
                  onClick={handleApprove}
                  loading={isApproving}
                  disabled={isApproving || isRejecting}
                >
                  {t('Утвердить')}
                </CustomButton>
              )}
              {canReject && (
                <CustomButton
                  variant="outlined"
                  color="error"
                  onClick={handleReject}
                  loading={isRejecting}
                  disabled={isApproving || isRejecting}
                >
                  {t('Отклонить')}
                </CustomButton>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="h6" mb={2}>
              {t('Список учеников')}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Статус')}</TableCell>
                    <TableCell>{t('ФИО ученика')}</TableCell>
                    <TableCell>{t('ИИН')}</TableCell>
                    <TableCell>{t('Класс')}</TableCell>
                    <TableCell>{t('Блюда')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voucher.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{getItemStatusText(item.status, t)}</TableCell>
                      <TableCell>{item.studentFullName}</TableCell>
                      <TableCell>{item.studentIin}</TableCell>
                      <TableCell>
                        {item.grade ? `${item.grade}${item.classLetter || ''}` : '-'}
                      </TableCell>
                      <TableCell>{getLocalizedText(item.menuItemsRu, item.menuItemsKk)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </PageWrapper>
  )
}
